import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import FormInput from "../components/common/FormInput";
import Button from "../components/common/Button";
import {
  useForgotPasswordMutation,
  useVerifyInitialOtpMutation,
  useResetPasswordWithOtpMutation,
} from "../services/auth/authApi";
import QuestionMark from "../assets/forgot-screen-icon.png";
import "@fontsource/work-sans/500.css";
import "@fontsource/work-sans/600.css";
import "@fontsource/work-sans/400.css";
import { AuthNavBar } from "../components/Navbar";
import OtpModal from "../components/common/OtpModal";
const ForgotPassword = () => {
  const [
    forgotPassword,
    { isLoading: isRequestingReset, error: forgotPasswordError },
  ] = useForgotPasswordMutation();
  const [verifyInitialOtp, { isLoading: isVerifyingOtp }] =
    useVerifyInitialOtpMutation();
  const [resetPasswordWithOtp, { isLoading: isResettingPassword }] =
    useResetPasswordWithOtpMutation();
  const [successMessage, setSuccessMessage] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showResetSuccessModal, setShowResetSuccessModal] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpInfoMessage, setOtpInfoMessage] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [resetPasswordError, setResetPasswordError] = useState("");
  const [resetPasswordMessage, setResetPasswordMessage] = useState("");
  const [resetSuccessMessage, setResetSuccessMessage] = useState(
    "Password reset successful.",
  );
  const otpInputsRef = useRef([]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onSubmit",
  });

  const onSubmit = async (data) => {
    try {
      const response = await forgotPassword(data).unwrap();
      localStorage.setItem("forgotPasswordEmail", data.email || "");
      setSuccessMessage(response?.message || "Reset link request sent.");
      setOtpValues(["", "", "", ""]);
      setOtpError("");
      setOtpInfoMessage("");
      setShowResetPasswordModal(false);
      setShowResetSuccessModal(false);
      setShowOtpModal(true);
    } catch (error) {
      setSuccessMessage("");
      console.error("Forgot password request failed", error);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextValues = [...otpValues];
    nextValues[index] = digit;
    setOtpValues(nextValues);
    setOtpError("");

    if (digit && index < 3) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otpValues.join("");
    if (!/^\d{4}$/.test(otpCode)) {
      setOtpError("Please enter a valid 4-digit OTP");
      return;
    }

    const forgotPasswordEmail =
      localStorage.getItem("forgotPasswordEmail") || "";
    if (!forgotPasswordEmail) {
      setOtpError("Email not found. Please request OTP again.");
      return;
    }

    try {
      const response = await verifyInitialOtp({
        email: forgotPasswordEmail,
        otp: otpCode,
      }).unwrap();

      localStorage.setItem("forgotPasswordOtp", otpCode);
      setOtpError("");
      setOtpInfoMessage(response?.message || "OTP verified.");
      setShowOtpModal(false);
      setResetPasswordError("");
      setResetPasswordMessage("");
      setNewPassword("");
      setConfirmNewPassword("");
      setShowResetPasswordModal(true);
    } catch (error) {
      setOtpError(error?.data?.message || "OTP verification failed.");
    }
  };

  const handleResetPasswordSubmit = async (event) => {
    event.preventDefault();

    if (!newPassword || !confirmNewPassword) {
      setResetPasswordError("Both password fields are required.");
      setResetPasswordMessage("");
      return;
    }

    if (newPassword.length < 8) {
      setResetPasswordError("Password must be at least 8 characters.");
      setResetPasswordMessage("");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setResetPasswordError("Passwords do not match.");
      setResetPasswordMessage("");
      return;
    }

    const forgotPasswordEmail =
      localStorage.getItem("forgotPasswordEmail") || "";
    const forgotPasswordOtp = localStorage.getItem("forgotPasswordOtp") || "";

    if (!forgotPasswordEmail || !forgotPasswordOtp) {
      setResetPasswordError("Email or OTP not found. Please verify OTP again.");
      setResetPasswordMessage("");
      return;
    }

    try {
      const response = await resetPasswordWithOtp({
        email: forgotPasswordEmail,
        otp: forgotPasswordOtp,
        newPassword,
        confirmNewPassword,
      }).unwrap();

      setResetPasswordError("");
      setResetPasswordMessage(
        response?.message || "Password reset successful.",
      );
      setShowResetPasswordModal(false);
      setResetSuccessMessage(response?.message || "Password reset successful.");
      setShowResetSuccessModal(true);
    } catch (error) {
      setResetPasswordError(
        error?.data?.message || "Failed to reset password. Please try again.",
      );
      setResetPasswordMessage("");
    }
  };

  const handleResendOtp = async () => {
    const forgotPasswordEmail =
      localStorage.getItem("forgotPasswordEmail") ||
      document.getElementById("resetIdentifier")?.value?.trim() ||
      "";

    if (!forgotPasswordEmail) {
      setOtpError("Please enter email or phone number first.");
      setOtpInfoMessage("");
      return;
    }

    try {
      const response = await forgotPassword({
        email: forgotPasswordEmail,
      }).unwrap();

      if (response?.success === false) {
        setOtpInfoMessage("");
        setOtpError(
          response?.message || "Failed to resend OTP. Please try again.",
        );
        return;
      }

      localStorage.setItem("forgotPasswordEmail", forgotPasswordEmail);
      localStorage.removeItem("forgotPasswordOtp");
      setOtpError("");
      setOtpInfoMessage(response?.message || "A new OTP has been sent.");
      setOtpValues(["", "", "", ""]);
      otpInputsRef.current[0]?.focus();
    } catch (error) {
      setOtpInfoMessage("");
      setOtpError(
        error?.data?.message || "Failed to resend OTP. Please try again.",
      );
    }
  };

  return (
    <div>
      <AuthNavBar/>
      <div className="h-screen bg-[linear-gradient(135deg,#F3D9DA_0%,#B3D9FF_100%)] px-6 pt-28">
        <div className="relative flex">
          <div className="mx-auto  flex w-full max-w-[420px] justify-center">
            <div className="rounded-[17px] bg-[#F5F5F5] px-[50px] pt-11 pb-16 shadow-[0_2px_5.7px_0_#00000026]">
              <h1 className="font-['Work_Sans'] text-center text-2xl font-semibold leading-7 tracking-[-2%] text-black">
                Forgot Your Password
              </h1>

              <p className="mt-5 font-['Work_Sans'] text-xs leading-2 text-black">
                Please enter the email address or phone no you&apos;d like your
                password reset information sent to
              </p>

              <form
                className="mt-5"
                noValidate
                onSubmit={handleSubmit(onSubmit)}
              >
                <FormInput
                  id="resetIdentifier"
                  label="Enter email or phone no*"
                  type="text"
                  placeholder="Registered email or phone no"
                  labelClassName="block font-['Work_Sans']  leading-[21px] text-xs  text-black"
                  inputClassName=" font-['Work_Sans'] bg-white h-12 rounded-lg text-[13px] border-[0.5px] border-[#923D8C] px-4 font-medium text-[#8C8CA1] placeholder:text-[#8C8CA1]"
                  {...register("email", {
                    required: "Email or phone number is required",
                  })}
                />
                {errors.email ? (
                  <p className="mt-1.5 text-sm font-medium text-[#C62828]">
                    {errors.email.message}
                  </p>
                ) : null}

                <Button
                  type="submit"
                  disabled={isSubmitting || isRequestingReset}
                  btnText={
                    isSubmitting || isRequestingReset
                      ? "Sending..."
                      : "Request Reset Link"
                  }
                  btnClassName="mt-4 h-8 font-['Work_Sans'] w-full rounded-full bg-[#923D8C] font-medium leading-[19px] text-white tracking-[4%] py-1.5"
                />

                {forgotPasswordError ? (
                  <p className="mt-3 text-sm font-medium text-[#C62828]">
                    {forgotPasswordError?.data?.message ||
                      "Failed to send reset request. Please try again."}
                  </p>
                ) : null}

                {successMessage ? (
                  <p className="mt-3 text-sm font-medium text-[#2E7D32]">
                    {successMessage}
                  </p>
                ) : null}
              </form>

              <Link
                to="/login"
                className="mt-4 font-['Work_Sans'] block text-center leading-[18px] text-[15px] text-[#000066]"
              >
                Back To Login
              </Link>
            </div>
          </div>
          <div className="absolute left-[63%] top-32">
            <img
              src={QuestionMark}
              alt="Forgot password illustration"
              className="h-[492px] w-[369px] object-contain"
            />
          </div>
        </div>
      </div>

      <OtpModal
        isOpen={showOtpModal}
        otpValues={otpValues}
        otpError={otpError}
        otpInfoMessage={otpInfoMessage}
        isLoading={isVerifyingOtp || isRequestingReset}
        onChange={handleOtpChange}
        onKeyDown={handleOtpKeyDown}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
        inputRefs={otpInputsRef}
      />

      {showResetPasswordModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-[420px] font-['Work_Sans']  text-black rounded-[17px] bg-[#F5F5F5] px-12 py-8 shadow-[0_20px_45px_#00000026]">
            <h2 className="text-center  text-2xl font-semibold leading-7 tracking-[-2%] ">
              Enter your new password
            </h2>

            <p className=" mt-[14px] max-w-[320px] tracking-[-5%] text-center  text-xs leading-4 text-[#8C8CA1]">
              Your new password must be different to previous passwords.
            </p>

            <form className="mt-[14px]" onSubmit={handleResetPasswordSubmit}>
              <label className="leading-[21px] ">
                Enter new password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  setResetPasswordError("");
                }}
                className="mt-1 h-12 w-full rounded-lg border-[0.5px] border-[#923D8C] bg-[#FAFCFE] px-4 text-base text-black focus:outline-none"
              />

              <label className="mt-[14px] block   leading-[21px] ">
                Confirm new password
              </label>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(event) => {
                  setConfirmNewPassword(event.target.value);
                  setResetPasswordError("");
                }}
                className="mt-1 h-12 w-full rounded-lg border-[0.5px] border-[#923D8C] bg-[#FAFCFE] px-4   focus:outline-none"
              />

              {resetPasswordError ? (
                <p className="mt-3 text-sm font-medium text-[#C62828]">
                  {resetPasswordError}
                </p>
              ) : null}

              {resetPasswordMessage ? (
                <p className="mt-3 text-sm font-medium text-[#2E7D32]">
                  {resetPasswordMessage}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={isResettingPassword}
                className="mt-[14px] h-8 w-full tracking-[4%] rounded-full bg-[#923D8C]   font-medium  text-white shadow-[0_6px_2px_-4px_#0E0E2C1A]"
              >
                {isResettingPassword ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {showResetSuccessModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-[420px] rounded-[18px] bg-[#F5F5F5] px-10 py-10 shadow-[0_20px_45px_#00000026]">
            <h2 className="text-center font-['Work_Sans'] text-2xl font-semibold leading-7 tracking-[-2%] text-black">
              Password Reset Successfully
            </h2>

            <p className="mt-3 text-center font-['Work_Sans'] text-sm leading-5 text-[#8C8CA1]">
              {resetSuccessMessage}
            </p>

            <div className="mt-6 flex justify-center">
              <Link
                to="/login"
                onClick={() => setShowResetSuccessModal(false)}
                className="h-8 w-full rounded-full bg-[#923D8C] text-center font-['Work_Sans'] font-medium leading-8 text-white shadow-[0_6px_2px_-4px_#0E0E2C1A]"
              >
                Back To Login
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ForgotPassword;
